/** Helpers for Phase D GET /entitlements/me on the plan settings page. */

export const CORE_PLATFORM_KEYS = [
  "voice_of_customer",
  "market_intelligence",
  "full_intelligence",
];

export const MARKETPLACE_ADDON_KEYS = [
  "customer_journey",
  "customer_365",
  "pivony_sonic_activation",
  "sonic_engage_enterprise",
  "industry_expert",
  "sonic_advisor_enterprise",
];

export const FRONT_DOOR_PRODUCT_KEYS = [
  "survey",
  "survey_enterprise",
  "sonic_prospect",
  "sonic_prospect_plus",
  "sonic_prospect_pro",
  "sonic_prospect_enterprise",
];

export const ENTERPRISE_SONIC_PRODUCT_KEYS = [
  "survey_enterprise",
  "sonic_prospect_enterprise",
  "sonic_engage_enterprise",
  "sonic_advisor_enterprise",
];

export const CREDIT_PRODUCT_KEYS = [
  "industry_expert",
  "genai_complete_analysis",
];

const ACTIVE_STATUSES = new Set(["active", "trial"]);

export function isEntitlementActive(entitlement) {
  return Boolean(entitlement && ACTIVE_STATUSES.has(entitlement.status));
}

export function entitlementByProductKey(entitlements = []) {
  const map = new Map();
  (entitlements || []).forEach((entry) => {
    const key = entry?.product_key || entry?.productKey;
    if (key) map.set(key, entry);
  });
  return map;
}

export function findCorePlatformEntitlement(entitlements = []) {
  for (const key of CORE_PLATFORM_KEYS) {
    const match = (entitlements || []).find(
      (entry) =>
        entry?.product_key === key && isEntitlementActive(entry),
    );
    if (match) return match;
  }
  return null;
}

/**
 * VoC / MI / Full Intelligence quotas (volume, dashboards, KPI alerts, AI credits
 * tied to core). Front-door-only orgs must not see these empty/default bars.
 */
export function shouldShowCorePlanSections(coreEntitlement) {
  return Boolean(coreEntitlement && isEntitlementActive(coreEntitlement));
}

export function isProductHeld(productKey, entitlements = []) {
  const byKey = entitlementByProductKey(entitlements);
  if (isEntitlementActive(byKey.get(productKey))) {
    return true;
  }
  if (
    productKey === "survey" &&
    (
      isEntitlementActive(byKey.get("survey_plus")) ||
      isEntitlementActive(byKey.get("survey_pro")) ||
      isEntitlementActive(byKey.get("survey_pro_plus")) ||
      isEntitlementActive(byKey.get("survey_enterprise"))
    )
  ) {
    return true;
  }
  if (
    productKey === "pivony_sonic_activation" &&
    (
      isEntitlementActive(byKey.get("pivony_sonic")) ||
      isEntitlementActive(byKey.get("sonic_engage_enterprise"))
    )
  ) {
    return true;
  }
  if (
    productKey === "sonic_prospect" &&
    (
      isEntitlementActive(byKey.get("sonic_prospect_plus")) ||
      isEntitlementActive(byKey.get("sonic_prospect_pro")) ||
      isEntitlementActive(byKey.get("sonic_prospect_pro_plus")) ||
      isEntitlementActive(byKey.get("sonic_prospect_enterprise"))
    )
  ) {
    return true;
  }
  if (
    productKey === "industry_expert" &&
    isEntitlementActive(byKey.get("sonic_advisor_enterprise"))
  ) {
    return true;
  }
  return false;
}

export function resolveVolumeLimit(entitlement) {
  if (!entitlement) return null;
  const override = entitlement.quota_override || {};
  if (override.volume_max != null && override.volume_max !== "") {
    const parsed = Number(override.volume_max);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (entitlement.volume_max != null) {
    const parsed = Number(entitlement.volume_max);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function resolveVolumeUsage(entitlement) {
  const used = Number(entitlement?.usage_current_period ?? 0);
  return Number.isFinite(used) ? used : 0;
}

export function findProductEntitlement(entitlements = [], productKey) {
  return (entitlements || []).find(
    (entry) =>
      entry?.product_key === productKey &&
      isEntitlementActive(entry),
  );
}

export function findSurveyEntitlement(entitlements = []) {
  return (
    findProductEntitlement(entitlements, "survey_enterprise") ||
    findProductEntitlement(entitlements, "survey_pro_plus") ||
    findProductEntitlement(entitlements, "survey_pro") ||
    findProductEntitlement(entitlements, "survey_plus") ||
    findProductEntitlement(entitlements, "survey")
  );
}

export function isSurveyFreeTier(entitlement) {
  if (!entitlement) return true;
  const key = String(entitlement.product_key || "");
  if (
    key === "survey_plus" ||
    key === "survey_pro" ||
    key === "survey_pro_plus" ||
    key === "survey_enterprise"
  ) {
    return false;
  }
  const tier = String(entitlement.tier_key || "free").toLowerCase();
  return tier === "free";
}

export function nextSurveyUpgradeTier(entitlements = []) {
  if (findProductEntitlement(entitlements, "survey_enterprise")) return null;
  if (findProductEntitlement(entitlements, "survey_pro_plus")) return null;
  if (findProductEntitlement(entitlements, "survey_pro")) return "pro_plus";
  if (findProductEntitlement(entitlements, "survey_plus")) return "pro";
  return "plus";
}

export function surveyProductKeyForTier(tier) {
  if (tier === "pro_plus") return "survey_pro_plus";
  if (tier === "pro") return "survey_pro";
  return "survey_plus";
}

export function isNearQuotaLimit(used, limit, threshold = 0.8) {
  if (limit == null || limit <= 0) return false;
  const safeUsed = Number(used);
  if (!Number.isFinite(safeUsed)) return false;
  return safeUsed >= limit * threshold;
}

export function isCoreTopTier(coreEntitlement) {
  if (!coreEntitlement?.tier_key) return false;
  return String(coreEntitlement.tier_key).toLowerCase() === "enterprise";
}

export function resolveDashboardQuota(plan) {
  const limit = Number(plan?.TopicBoardsLimit ?? 0);
  const usedRaw = Number(plan?.TopicBoardsUsed);
  const remaining = Number(plan?.RemainingTopicBoards ?? 0);
  const used = Number.isFinite(usedRaw)
    ? usedRaw
    : limit > 0
      ? Math.max(0, limit - (Number.isFinite(remaining) ? remaining : 0))
      : 0;
  return {
    used,
    limit: limit > 0 ? limit : null,
    remaining: Number.isFinite(remaining) ? remaining : null,
  };
}

export function filterMarketplaceAddons(products = []) {
  return resolvePlanCatalogProducts(products, MARKETPLACE_ADDON_KEYS);
}

export function filterFrontDoorProducts(products = []) {
  return resolvePlanCatalogProducts(products, FRONT_DOOR_PRODUCT_KEYS);
}

const PROSPECT_FRONT_DOOR_KEYS = [
  "sonic_prospect",
  "sonic_prospect_plus",
  "sonic_prospect_pro",
];

function hasAnySonicProspectEntitlement(entitlements = []) {
  return (
    isProductHeld("sonic_prospect", entitlements) ||
    isProductHeld("sonic_prospect_plus", entitlements) ||
    isProductHeld("sonic_prospect_pro", entitlements) ||
    isProductHeld("sonic_prospect_pro_plus", entitlements) ||
    isProductHeld("sonic_prospect_enterprise", entitlements)
  );
}

/**
 * VoC add-ons (Journey, 365, Sonic Engage, Advisor Pro) require a core
 * platform base. Prospect-only orgs must not see this marketplace.
 */
export function visibleMarketplaceAddonKeys(entitlements = []) {
  if (!findCorePlatformEntitlement(entitlements)) {
    return [];
  }
  return [...MARKETPLACE_ADDON_KEYS];
}

/**
 * Front-door catalog by held products:
 * - Survey: only if already held or org has core VoC/MI/Full
 * - Sonic Prospect keys: only when Prospect is not already held (dedicated billing section owns it)
 */
export function visibleFrontDoorProductKeys(entitlements = []) {
  const hasCore = Boolean(findCorePlatformEntitlement(entitlements));
  const hasProspect = hasAnySonicProspectEntitlement(entitlements);
  const hasSurvey =
    isProductHeld("survey", entitlements) ||
    isProductHeld("survey_plus", entitlements);
  const keys = [];

  if (hasSurvey || hasCore) {
    keys.push("survey");
  }
  if (!hasProspect) {
    keys.push(...PROSPECT_FRONT_DOOR_KEYS);
  }
  return keys;
}

export function filterMarketplaceAddonsForOrg(products = [], entitlements = []) {
  const keys = visibleMarketplaceAddonKeys(entitlements);
  if (!keys.length) return [];
  return resolvePlanCatalogProducts(products, keys);
}

export function filterFrontDoorProductsForOrg(products = [], entitlements = []) {
  const keys = visibleFrontDoorProductKeys(entitlements);
  if (!keys.length) return [];
  return resolvePlanCatalogProducts(products, keys);
}

/** Merge API catalog with stable key ordering; fill gaps when API catalog is incomplete. */
export function resolvePlanCatalogProducts(products = [], orderedKeys = []) {
  const byKey = new Map();
  (products || []).forEach((product) => {
    const key = product?.product_key || product?.key;
    if (key) byKey.set(key, product);
  });

  const keys = orderedKeys.length
    ? orderedKeys
    : [...byKey.keys()];

  return keys.map((key) => {
    if (byKey.has(key)) return byKey.get(key);
    return {
      product_key: key,
      key,
      name: key,
      description: "",
      category: FRONT_DOOR_PRODUCT_KEYS.includes(key) ? "front_door" : "addon",
    };
  });
}

export function formatTierLabel(tierKey) {
  if (!tierKey) return "";
  return String(tierKey)
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

export function creditPoolUsed(pool) {
  const bundle = Number(pool?.credit_bundle_size ?? 0);
  const balance = Number(pool?.credit_balance ?? 0);
  if (!Number.isFinite(bundle) || bundle <= 0) return 0;
  const used = bundle - (Number.isFinite(balance) ? balance : 0);
  return Math.max(0, Math.min(bundle, used));
}

export function buildContactTicketPrefill({ productKey, productName, t }) {
  const label = productName || productKey || "product";
  return t("settings.plan_contact_prefill", {
    product: label,
    productKey: productKey || "",
    defaultValue: `Hello,\n\nI would like information or activation for "${label}" (${productKey}).\n\nThank you.`,
  });
}

const PLAN_PRODUCT_I18N = {
  customer_journey: {
    name: "settings.plan_product_customer_journey_name",
    description: "settings.plan_product_customer_journey_desc",
  },
  customer_365: {
    name: "settings.plan_product_customer_365_name",
    description: "settings.plan_product_customer_365_desc",
  },
  pivony_sonic_activation: {
    name: "settings.plan_product_sonic_name",
    description: "settings.plan_sonic_description",
  },
  sonic_engage_enterprise: {
    name: "settings.plan_product_sonic_engage_enterprise_name",
    description: "settings.plan_product_sonic_engage_enterprise_desc",
  },
  industry_expert: {
    name: "settings.plan_product_advisor_pro_name",
    description: "settings.plan_product_advisor_pro_desc",
  },
  sonic_advisor_enterprise: {
    name: "settings.plan_product_sonic_advisor_enterprise_name",
    description: "settings.plan_product_sonic_advisor_enterprise_desc",
  },
  survey: {
    name: "settings.plan_product_survey_name",
    description: "settings.plan_product_survey_desc",
  },
  survey_enterprise: {
    name: "settings.plan_product_survey_enterprise_name",
    description: "settings.plan_product_survey_enterprise_desc",
  },
  sonic_prospect_enterprise: {
    name: "settings.plan_product_sonic_prospect_enterprise_name",
    description: "settings.plan_product_sonic_prospect_enterprise_desc",
  },
};

/** Localized marketplace / front-door card copy (API description is English-only). */
export function resolvePlanProductCopy(productKey, product, t) {
  const keys = PLAN_PRODUCT_I18N[productKey];
  const fallbackName = product?.name || productKey || "";
  const fallbackDesc = product?.description || "";
  return {
    name: keys?.name ? t(keys.name, fallbackName) : fallbackName,
    description: keys?.description
      ? t(keys.description, fallbackDesc)
      : fallbackDesc,
  };
}
